<template>
    <v-card class="mt-4">
        <v-card-title>
            <v-row align="center">
                <v-col class="grow ma-2"> Task References </v-col>
                <v-col class="shrink text-right">
                    <v-btn color="primary" class="ma-2" @click="showCreateTaskReferenceForm">
                        New Task Reference
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-title>
        <v-data-table
            :headers="taskReferenceHeaders"
            :items="taskReferenceListItems"
            no-results-text="No task references."
            item-key="id"
            class="elevation-1"
            density="compact"
            :items-per-page="25"
        >
            <template v-slot:item.edit="{ item }">
                <v-icon @click="showEditTaskReference(item)">$pencil</v-icon>
            </template>
            <template v-slot:item.delete="{ item }">
                <v-icon @click="deleteTaskReference(item)">$delete</v-icon>
            </template>
        </v-data-table>
        <v-dialog v-model="showTaskReferenceForm" width="1000" persistent>
            <task-reference-form
                v-if="selectedTaskReference !== null"
                :protocol="protocol"
                :experiment="experiment"
                :task-reference="selectedTaskReference"
                @closed="taskReferenceFormClosed"
                @saved="taskReferenceSaved"
            />
        </v-dialog>
    </v-card>
</template>
<script>
import { defineComponent } from "vue";

import TaskReferenceForm from "./TaskReferenceForm.vue";
import { buildTaskReferencesAPIForExperiment } from "@/services/api.services";
import { EventBus } from "@/lib/event-bus";
import { getAttribute, getThingSchemaById } from "@/lib/thingUtils";

export default defineComponent({
    components: { TaskReferenceForm },

    props: {
        taskReferences: {
            type: Array,
            required: true,
        },
        protocol: {
            type: Object, // AbstractSchemaConfig
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            selectedTaskReference: null,
            showTaskReferenceForm: false,
            taskReferenceHeaders: [
                {
                    title: "Name",
                    value: "name",
                    align: "left",
                    sortable: false,
                    width: "25%",
                },
                {
                    title: "Days after reference",
                    value: "daysAfterReference",
                    sortable: false,
                    align: "left",
                    width: "10%",
                },
                {
                    title: "Reference",
                    value: "reference",
                    sortable: false,
                    align: "left",
                    width: "10%",
                },
                {
                    title: "Edit",
                    value: "edit",
                    sortable: false,
                    align: "left",
                    width: "5%",
                },
                {
                    title: "Delete",
                    value: "delete",
                    sortable: false,
                    align: "left",
                    width: "5%",
                },
            ],
        };
    },

    mounted() {
        this.taskReferencesAPI = buildTaskReferencesAPIForExperiment(this.experiment.code);
    },

    computed: {
        taskReferenceListItems() {
            return this.taskReferences.map((taskReference) => {
                const reference = this.getReference(taskReference);
                return {
                    ...taskReference,
                    taskReference,
                    reference,
                };
            });
        },
    },

    methods: {
        getReference(taskReference) {
            if (taskReference.referenceExperimentStartDate) {
                return "Experiment start date";
            } else {
                const schema = getThingSchemaById(this.protocol, taskReference.thingSchemaId);
                const attribute = getAttribute(schema, taskReference.referenceAttributeName);
                return `${schema.displayName} ${attribute.display_name}`;
            }
        },
        showCreateTaskReferenceForm() {
            this.selectedTaskReference = {
                name: "",
                daysAfterReference: null,
                referenceExperimentStartDate: false,
                thingSchemaId: null,
                referenceAttributeName: "",
            };
            this.showTaskReferenceForm = true;
        },
        showEditTaskReference(taskReference) {
            this.selectedTaskReference = taskReference;
            this.showTaskReferenceForm = true;
        },
        taskReferenceFormClosed() {
            this.resetTaskReferenceForm();
        },
        taskReferenceSaved(taskReference) {
            this.resetTaskReferenceForm();
            this.$emit("saved", taskReference);
        },
        resetTaskReferenceForm() {
            this.selectedTaskReference = null;
            this.showTaskReferenceForm = false;
        },
        deleteTaskReference(taskReference) {
            if (confirm("Are you sure you want to delete this task reference?")) {
                this.taskReferencesAPI
                    .delete(taskReference.id)
                    .then(() => {
                        this.$emit("deleted", taskReference);
                    })
                    .catch((err) => {
                        EventBus.$emit("globalAlert", err);
                    });
            }
        },
    },
});
</script>
<style></style>
